<template>
  <VaClientSideList
    class="va-table"
    :headers="statisticsTableHeaders"
    :items="hasDataSource ? statisticsTableItems : []"
    :no-data-text="noDataText"
    :loading="statisticsLoading"
    hide-footer
  >
    <template v-slot:item="{ item }">
      <template v-if="item.name === $t('resources.ab_tests.difference')">
        <tr class="custom-row" :class="{ 'row--expandable': byState }" @click="toggleRow(item)">
          <template v-for="header in statisticsTableHeaders">
            <td :key="`diff-${header.value}`" :class="header.align ? `text-${header.align}` : ''">
              <template v-if="header.value === 'name'">
                <div class="custom-row__headline">
                  <v-icon v-if="byState" small class="expand-icon">
                    {{ isExpanded(item) ? "mdi-chevron-up" : "mdi-chevron-down" }}
                  </v-icon>
                  {{ item.name }}
                </div>
              </template>

              <template v-else>
                <MetricCard
                  :metricData="getDiffValue(item, header.value)"
                  :invert-colors="header.value === 'noConnect'" />
              </template>
            </td>
          </template>
        </tr>
      </template>

      <template v-else>
        <tr :class="{ 'row--expandable': byState }" @click="toggleRow(item)">
          <td
            v-for="header in statisticsTableHeaders"
            :key="`data-${header.value}`"
            :class="header.align ? `text-${header.align}` : ''">
            <template v-if="header.value === 'name'">
              <div class="variant-name-cell">
                <v-icon v-if="byState" small class="expand-icon">
                  {{ isExpanded(item) ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
                <span class="variant-name-cell__name">{{ item.name }}</span>
                <AbTestVariantSourceLabel :label="item.sourceLabel" />
              </div>
            </template>

            <template v-else>
              <div :class="header.align ? `text-${header.align}` : ''">
                <span>{{ formatValue(item, header) }}</span>
              </div>
            </template>
          </td>
        </tr>
      </template>

      <AbTestStateRow
        v-for="state in expandedStates(item)"
        :key="`state-${rowKey(item)}-${state.state}`"
        :state="state"
        :statistics-table-headers="statisticsTableHeaders"
        :is-difference="isDifferenceRow(item)"
        :is-highlighted="hoveredState === state.state"
        @hover="hoveredState = $event" />

      <tr v-if="exceedsStatesLimit(item)" :key="`more-${rowKey(item)}`" class="state-more-row">
        <td :colspan="statisticsTableHeaders.length">
          <v-btn
            text
            small
            color="primary"
            class="state-more-row__button"
            :ripple="false"
            @click="toggleAllStates(item)">
            {{ $t(showsAllStates(item) ? "resources.ab_tests.show_less" : "resources.ab_tests.show_all") }}
            <v-icon small right>{{ showsAllStates(item) ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
          </v-btn>
        </td>
      </tr>

      <tr v-if="showNoStateData(item)" :key="`empty-${rowKey(item)}`" class="state-empty-row">
        <td :colspan="statisticsTableHeaders.length">
          {{ $t("resources.ab_tests.states_filter.no_data") }}
        </td>
      </tr>
    </template>
  </VaClientSideList>
</template>

<script>
  import VaClientSideList from "@/components/lists/VaClientSideList";
  import MetricCard from "@/components/custom/MetricCard";
  import AbTestVariantSourceLabel from "@/components/custom/AbTest/AbTestVariantSourceLabel";
  import AbTestStateRow from "@/components/custom/AbTest/AbTestStateRow";
  import { formatSecondsToHHMMSS } from "@/utility/formatDateTime/formatDateTime";
  import { formatNumber } from "@/@core/utils/filter";
  import i18n from "@/i18n";
  import abTestStateRows from "@/mixins/abTestStateRows";

  export default {
    name: "AbTestStatisticsSummaryView",
    components: {
      VaClientSideList,
      MetricCard,
      AbTestVariantSourceLabel,
      AbTestStateRow,
    },
    mixins: [abTestStateRows],
    props: {
      statisticsTableHeaders: {
        type: Array,
        required: true,
      },
      statisticsTableItems: {
        type: Array,
        required: true,
      },
      statisticsLoading: {
        type: Boolean,
        required: true,
      },
      showCallsData: {
        type: Boolean,
        default: true,
      },
      showVoluumData: {
        type: Boolean,
        default: true,
      },
    },
    computed: {
      hasDataSource() {
        return this.showCallsData || this.showVoluumData;
      },
      noDataText() {
        if (!this.hasDataSource && this.statisticsTableItems.length > 0) {
          return i18n.t("resources.ab_tests.empty_data_source");
        }

        return i18n.t("generic.no_data_available");
      },
    },
    methods: {
      getDiffValue(item, headerValue) {
        const diffKey = `${headerValue}Diff`;

        return item[diffKey];
      },
      formatValue(item, header) {
        const value = item[header.value];

        if (value === undefined || value === null || value === '') {
          return '-';
        }

        if (header.value === 'revenue' || header.value === 'rpc' || header.value === 'epv' || header.value === 'cv') {
          return `$${formatNumber(value)}`;
        }

        if (header.value === 'acl') {
          return formatSecondsToHHMMSS(value);
        }

        if (header.value === 'callsCr' || header.value === 'sessionsCr') {
          return `${value}%`;
        }

        return formatNumber(value);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .va-table {
    &::v-deep {
      table {
        border-top: none !important;
        border-bottom: none !important;

        .custom-row {
          background-color: get-color("hover");
          td {
            border-bottom: 1px solid get-color("border");
          }
          &__headline {
            display: flex;
            align-items: center;
            text-transform: uppercase;
            color: get-color("subtitle");
            @include text-caption-bold();
          }
        }

        .row--expandable {
          cursor: pointer;

          &:hover td {
            background-color: get-color("hover");
          }
        }

        .state-empty-row td {
          text-align: center;
          font-style: italic;
          color: get-color("subtitle");
          background-color: get-color("element-bg-200");
        }

        .state-more-row {
          td {
            background-color: get-color("element-bg-200");
          }

          &__button {
            margin-left: 12px;
            @include text-button-medium();
          }
        }
      }
    }
  }

  .variant-name-cell {
    display: flex;
    align-items: center;
    gap: 8px;

    &__name {
      @include text-body-2-medium();
    }
  }

  .expand-icon {
    margin-right: 4px;
  }
</style>
